import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { createReview, getPropertyTypes } from '../api/client';

export default function NewReview() {
  const navigate = useNavigate();
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [form, setForm] = useState({
    property_address: '',
    property_type:    'standard_apartment',
    seller_type:      'private',
    agent_name:       '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState('');

  useEffect(() => { getPropertyTypes().then(setPropertyTypes); }, []);

  function update(field, value) { setForm(f => ({ ...f, [field]: value })); }

  async function submit(e) {
    e.preventDefault();
    if (!form.property_address.trim()) { setError('Please enter the property address.'); return; }
    setSubmitting(true);
    try {
      const review = await createReview(form);
      navigate(`/reviews/${review.id}`);
    } catch {
      setError('Something went wrong. Please try again.');
      setSubmitting(false);
    }
  }

  const isApartment = form.property_type.includes('apartment');
  const isCorporate = form.seller_type === 'corporate';

  return (
    <div className="page-narrow">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="page-header">
          <h1>New Analysis</h1>
          <p>Enter the property details to start a new dossier review.</p>
        </div>

        <div className="card">
          <form onSubmit={submit}>
            {/* Address */}
            <div className="form-group">
              <label className="form-label">Property address *</label>
              <input
                className="form-control"
                placeholder="e.g. Keizersgracht 123, 1015 CJ Amsterdam"
                value={form.property_address}
                onChange={e => update('property_address', e.target.value)}
                autoFocus
              />
            </div>

            {/* Property type */}
            <div className="form-group">
              <label className="form-label">Property type</label>
              <select
                className="form-control"
                value={form.property_type}
                onChange={e => update('property_type', e.target.value)}
              >
                {propertyTypes.map(pt => (
                  <option key={pt.id} value={pt.id}>{pt.label}</option>
                ))}
              </select>
            </div>

            {/* Seller type */}
            <div className="form-group">
              <label className="form-label">Seller type</label>
              <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                {[
                  { value: 'private',   label: 'Private individual' },
                  { value: 'corporate', label: 'Company / BV' },
                ].map(opt => (
                  <label key={opt.value} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    cursor: 'pointer', fontSize: '0.95rem',
                    padding: '12px 18px', border: '1px solid',
                    borderColor: form.seller_type === opt.value ? 'var(--navy)' : 'var(--border)',
                    borderRadius: 10, flex: 1,
                    background: form.seller_type === opt.value ? 'rgba(28,43,74,0.04)' : '#fff',
                    transition: 'all 0.15s',
                  }}>
                    <input
                      type="radio" name="seller_type" value={opt.value}
                      checked={form.seller_type === opt.value}
                      onChange={e => update('seller_type', e.target.value)}
                      style={{ accentColor: 'var(--navy)' }}
                    />
                    <span style={{ fontWeight: form.seller_type === opt.value ? 500 : 400 }}>
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Advisor name */}
            <div className="form-group">
              <label className="form-label">Advisor name</label>
              <input
                className="form-control"
                placeholder="e.g. Jan de Vries"
                value={form.agent_name}
                onChange={e => update('agent_name', e.target.value)}
              />
            </div>

            {/* Info banners */}
            {isApartment && (
              <div style={{
                background: 'rgba(28,43,74,0.04)', border: '1px solid rgba(28,43,74,0.12)',
                borderRadius: 10, padding: '12px 16px', marginBottom: 20,
                fontSize: 13, color: 'var(--navy)', fontWeight: 500,
              }}>
                ℹ️ VvE documents required — splitsingsakte, notulen, jaarrekening, MJOP etc.
              </div>
            )}

            {isCorporate && (
              <div style={{
                background: 'rgba(28,43,74,0.04)', border: '1px solid rgba(28,43,74,0.12)',
                borderRadius: 10, padding: '12px 16px', marginBottom: 20,
                fontSize: 13, color: 'var(--navy)', fontWeight: 500,
              }}>
                ℹ️ Corporate seller — KvK extract and UBO register declaration required.
              </div>
            )}

            {error && (
              <div style={{
                background: 'rgba(231,76,60,0.06)', color: 'var(--red)',
                border: '1px solid rgba(231,76,60,0.2)', borderRadius: 10,
                padding: '12px 16px', marginBottom: 20, fontSize: 14,
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 12 }}>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Creating...' : 'Create Analysis →'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => navigate('/')}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
